import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, IconButton, Typography, styled } from "@mui/material";
import React from "react";

const PillContainer = styled(Box)<{ isExpanded?: boolean }>(
    ({ isExpanded = false, theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
        padding: "12px 20px 12px 20px",
        paddingRight: "12px",
        marginBottom: isExpanded ? 0 : "10px",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
    })
);

const ContentContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    marginBottom: "10px",
    marginTop: 0,
    overflow: "hidden",
}));

interface ExpandableFieldSetProps {
    title: string;
    isExpanded?: boolean;
    onToggle?: () => void;
    children: React.ReactNode;
}

export const ExpandableFieldSet: React.FC<ExpandableFieldSetProps> = React.memo(
    ({ title, isExpanded: controlledIsExpanded, onToggle, children }) => {
        const [internalIsExpanded, setInternalIsExpanded] =
            React.useState(false);

        // Use controlled state if provided, otherwise use internal state
        const isExpanded =
            controlledIsExpanded !== undefined
                ? controlledIsExpanded
                : internalIsExpanded;

        const handleToggle = () => {
            if (onToggle) {
                onToggle();
            } else {
                setInternalIsExpanded(!internalIsExpanded);
            }
        };

        return (
            <Box sx={{ width: "100%" }}>
                <PillContainer isExpanded={isExpanded} onClick={handleToggle}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            gap: "0.5rem",
                            maxWidth: "calc(100% - 40px)",
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                                lineHeight: 1.4,
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>

                    <IconButton
                        size="small"
                        sx={{
                            color: (theme) =>
                                theme.palette.primary.contrastText,
                            padding: "4px",
                            flexShrink: 0,
                            ml: 1,
                            mr: 0,
                            "&:hover": {
                                backgroundColor: (theme) =>
                                    theme.palette.action.hover,
                            },
                            "& .MuiSvgIcon-root": {
                                fontSize: "1rem",
                            },
                        }}
                    >
                        {isExpanded ? <ExpandMore /> : <ChevronRight />}
                    </IconButton>
                </PillContainer>

                <Collapse in={isExpanded}>
                    <ContentContainer>{children}</ContentContainer>
                </Collapse>
            </Box>
        );
    }
);
